import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { isBoolean } from '@utils/common';

import { TTechnologyNameTitle } from '@interfaces/common/common';

import { ETechnologiesValues } from '@enums/common';

import { columnSizes } from '@constants/table';
import { editTooltipText, removeTooltipText } from './constants';

import styles from '@components/common/Table/Table.module.scss';

export const getColumns = ({
	technology,
	isEditAccess,
	toggleCellModal,
	setEditableCell,
	openDeleteCellConfirm,
	setSingleIdForRemove,
}: {
	technology?: TTechnologyNameTitle & { node: string };
	isEditAccess: boolean;
	toggleCellModal: () => void;
	setEditableCell: React.Dispatch<React.SetStateAction<object | null>>;
	openDeleteCellConfirm: () => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;

}) => ([
	{
		header: 'cell ID',
		accessorKey: 'cellId',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'zone',
		accessorKey: 'zone',
		enableSorting: false,
		isHidden: technology?.name === ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.title} />
			);
		},
	},
	{
		header: 'cell name',
		accessorKey: 'name',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value} />
			);
		},
	},
	{
		header: 'band',
		accessorKey: 'band4g',
		enableSorting: false,
		isHidden: technology?.name === ETechnologiesValues.T_5G,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const from = original.band4g?.frequencyRange?.from;
			const to = original.band4g?.frequencyRange?.to;

			return <AdaptiveTableCell text={original.band4g ? `${value?.title} (${from} - ${to})` : value?.title} />;
		},
	},
	{
		header: 'band',
		accessorKey: 'band5g',
		enableSorting: false,
		isHidden: technology?.name !== ETechnologiesValues.T_5G,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const from = original.band5g?.frequencyRange?.from;
			const to = original.band5g?.frequencyRange?.to;

			return <AdaptiveTableCell text={original.band5g ? `${value?.title} (${from} - ${to})` : value?.title} />;
		},
	},
	{
		header: 'antenna model',
		accessorKey: 'antennaModel',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.title} />
			);
		},
	},
	{
		header: 'height',
		accessorKey: 'height',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'azimuth',
		accessorKey: 'azimuth',
		size: columnSizes.size100,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'mechanical tilt',
		accessorKey: 'mechanicalTilt',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'electrical tilt',
		accessorKey: 'electricalTilt',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'total tilt',
		accessorKey: 'totalTilt',
		enableSorting: false,
		isHidden: technology?.name === ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'earfcn',
		accessorKey: 'earfcn',
		enableSorting: false,
		isHidden: technology?.name === ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'tac',
		accessorKey: 'tac',
		enableSorting: false,
		isHidden: technology?.name === ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'pci',
		accessorKey: 'pci',
		enableSorting: false,
		isHidden: technology?.name === ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: () => (
			<>
				Mechanical Tilt
				<br />
				Bracket
			</>
		),
		accessorKey: 'mechanicalTiltBracket',
		size: columnSizes.size170,
		enableSorting: false,
		isHidden: technology?.name !== ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return isBoolean(value) && (
				<AdaptiveTableCell text={value ? 'yes' : 'no'} />
			);
		},
	},
	{
		header: () => (
			<>
				Mechanical Tilt
				<br />
				Bracket Mount
			</>
		),
		accessorKey: 'mechanicalTiltBracketMount',
		enableSorting: false,
		isHidden: technology?.name !== ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: 'Spinning Range',
		accessorKey: 'spinningRange',
		enableSorting: false,
		isHidden: technology?.name !== ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value} />
			);
		},
	},
	{
		header: 'nrarfcn',
		accessorKey: 'nrarfcn',
		enableSorting: false,
		isHidden: technology?.name !== ETechnologiesValues.T_5G,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.toString()} />
			);
		},
	},
	{
		header: () => (
			<div className={styles.centerColumnHeader}>
				Actions
			</div>
		),
		accessorKey: 'id',
		enableSorting: false,
		size: columnSizes.size95,
		cell: ({ row }: CellContext<any, any>) => {
			const handleDelete = () => {
				setSingleIdForRemove(row?.original?.cellId);
				openDeleteCellConfirm();
			};

			const handleEdit = () => {
				if (!isEditAccess) return;

				setEditableCell(row?.original);
				toggleCellModal();
			};

			return (
				<Actions
					deleteTooltip={removeTooltipText}
					editHandler={handleEdit}
					editTooltip={editTooltipText}
					handleDelete={handleDelete}
					isDelete
					isDeleteDisable={!isEditAccess}
					isEdit
					isEditDisable={!isEditAccess}
				/>
			);
		},
	},
]);
